'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';
import { cn } from '../util/classes.ts';

export type FieldSetProps = ComponentProps<'fieldset'>;

export function FieldSet({ className, ...props }: FieldSetProps): ReactElement {
    return (
        <fieldset
            data-slot="field-set"
            className={cn(
                'flex flex-col gap-relaxed',
                'has-[>[data-slot=checkbox-group]]:gap-default',
                'has-[>[data-slot=radio-group]]:gap-default',
                className,
            )}
            {...props}
        />
    );
}

export type FieldLegendProps = ComponentProps<'legend'> & { variant?: 'legend' | 'label' };

export function FieldLegend({ className, variant = 'legend', ...props }: FieldLegendProps): ReactElement {
    return (
        <legend
            data-slot="field-legend"
            data-variant={variant}
            className={cn(
                'mb-1.5 font-medium data-[variant=label]:text-ui-sm data-[variant=legend]:text-ui-base',
                className,
            )}
            {...props}
        />
    );
}

export type FieldGroupProps = ComponentProps<'div'>;

export function FieldGroup({ className, ...props }: FieldGroupProps): ReactElement {
    return (
        <div
            data-slot="field-group"
            className={cn(
                'group/field-group @container/field-group',
                'flex w-full flex-col gap-5',
                'data-[slot=checkbox-group]:gap-default',
                '*:data-[slot=field-group]:gap-relaxed',
                className,
            )}
            {...props}
        />
    );
}

const fieldVariants = cva('group/field flex w-full gap-cozy state-invalid:text-danger', {
    variants: {
        orientation: {
            vertical: 'flex-col *:w-full [&>.sr-only]:w-auto',
            horizontal: [
                'flex-row items-center',
                'has-[>[data-slot=field-content]]:items-start',
                '*:data-[slot=field-label]:flex-auto',
                'has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
            ],
            responsive: [
                'flex-col',
                '*:w-full',
                // known issue: container queries aren't supported by the earliest versions of
                // Safari in our support policy
                '@md/field-group:flex-row',
                '@md/field-group:items-center',
                '@md/field-group:*:w-auto',
                '@md/field-group:has-[>[data-slot=field-content]]:items-start',
                '@md/field-group:*:data-[slot=field-label]:flex-auto',
                '[&>.sr-only]:w-auto',
                '@md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
            ],
        },
    },
    defaultVariants: {
        orientation: 'vertical',
    },
});

export type FieldProps = ComponentProps<'div'> & VariantProps<typeof fieldVariants>;

export function Field({ className, orientation = 'vertical', ...props }: FieldProps): ReactElement {
    return (
        <div
            role="group"
            data-slot="field"
            data-orientation={orientation}
            className={cn(fieldVariants({ orientation }), className)}
            {...props}
        />
    );
}

export type FieldContentProps = ComponentProps<'div'>;

export function FieldContent({ className, ...props }: FieldContentProps): ReactElement {
    return (
        <div
            data-slot="field-content"
            className={cn('group/field-content flex flex-1 flex-col gap-0.5 leading-snug', className)}
            {...props}
        />
    );
}

export type FieldLabelProps = ComponentProps<'label'>;

export function FieldLabel({ className, ...props }: FieldLabelProps): ReactElement {
    return (
        <label
            data-slot="field-label"
            className={cn(
                'group/field-label peer/field-label flex w-fit gap-cozy leading-snug',
                'text-ui-sm font-normal text-content',
                'group-state-disabled/field:opacity-disabled',
                'has-data-checked:border-content-weaker',
                'has-data-checked:bg-container-inset',
                'has-[input:checked]:border-content-weaker',
                'has-[input:checked]:bg-container-inset',
                'has-data-checked:hover:border-content-weak',
                'has-data-checked:hover:bg-container-inset',
                'has-[input:checked]:hover:border-content-weak',
                'has-[input:checked]:hover:bg-container-inset',
                'has-[>[data-slot=field]]:border',
                'has-[>[data-slot=field]]:rounded-default',
                'has-[[data-slot=checkbox],[data-slot=switch]]:cursor-pointer',
                'has-[[data-slot=checkbox],[data-slot=switch]]:select-none',
                'has-[[data-slot=checkbox],[data-slot=switch]]:hover:bg-container-hover',
                '*:data-[slot=field]:p-default',
                'has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col',
                className,
            )}
            {...props}
        />
    );
}

export type FieldTitleProps = ComponentProps<'div'>;

export function FieldTitle({ className, ...props }: FieldTitleProps): ReactElement {
    return (
        <div
            data-slot="field-label"
            className={cn(
                'flex w-fit items-center gap-cozy text-ui-sm font-medium',
                'group-state-disabled/field:opacity-disabled',
                className,
            )}
            {...props}
        />
    );
}

export type FieldDescriptionProps = ComponentProps<'p'>;

export function FieldDescription({ className, ...props }: FieldDescriptionProps): ReactElement {
    return (
        <p
            data-slot="field-description"
            className={cn(
                'text-left text-ui-sm leading-normal font-normal text-content-weak',
                'group-has-data-horizontal/field:text-balance',
                '[[data-variant=legend]+&]:-mt-1.5',
                'last:mt-0 nth-last-2:-mt-1',
                '[&>a]:underline [&>a]:underline-offset-4',
                '[&>a:hover]:text-brand',
                className,
            )}
            {...props}
        />
    );
}

export type FieldSeparatorProps = ComponentProps<'div'> & { children?: ReactNode };

export function FieldSeparator({ children, className, ...props }: FieldSeparatorProps): ReactElement {
    return (
        <div
            data-slot="field-separator"
            data-content={!!children}
            className={cn(
                'relative -my-2 h-5 text-ui-sm',
                'group-data-[variant=outline]/field-group:-mb-2',
                className,
            )}
            {...props}
        >
            <hr
                aria-orientation="horizontal"
                className="absolute inset-0 top-1/2 m-0 border-0 border-t border-line"
            />
            {children && (
                <span
                    className="relative mx-auto block w-fit bg-container px-cozy text-content-weak"
                    data-slot="field-separator-content"
                >
                    {children}
                </span>
            )}
        </div>
    );
}

export type FieldErrorProps = ComponentProps<'div'> & {
    errors?: ({ message?: string } | undefined)[];
};

export function FieldError({ className, children, errors, ...props }: FieldErrorProps): ReactElement | null {
    const content = useMemo(() => {
        if (children) {
            return children;
        }

        if (!errors?.length) {
            return null;
        }

        const uniqueErrors = [...new Map(errors.map((error) => [error?.message, error])).values()];

        if (uniqueErrors.length == 1) {
            return uniqueErrors[0]?.message;
        }

        return (
            <ul className="flex list-none flex-col gap-tight">
                {uniqueErrors.map((error, index) => error?.message && <li key={index}>{error.message}</li>)}
            </ul>
        );
    }, [children, errors]);

    if (!content) {
        return null;
    }

    return (
        <div
            role="alert"
            data-slot="field-error"
            className={cn('text-ui-sm font-normal text-danger', className)}
            {...props}
        >
            {content}
        </div>
    );
}
